import { PLAYER } from '../core/config';
import { clamp } from '../core/math';
import type { Vec2 } from '../geo/types';
import type { ControlState, InputFrame } from '../controls/types';
import type { CollisionWorld } from '../nav/types';
import { nearestNode, type WalkNet } from '../world/life/crowd/walkNet';
import { routeTree } from '../world/life/crowd/routes';
import type { PeerPose } from './peers';

const gap = (a: Vec2, b: Vec2): number => Math.hypot(a[0] - b[0], a[1] - b[1]);

function manualMovement(frame: InputFrame): boolean {
  return !!(
    frame.forward ||
    frame.right ||
    frame.up ||
    frame.turn ||
    frame.lookDx ||
    frame.actions.length
  );
}

export function clearFollowPath(collision: CollisionWorld, a: Vec2, b: Vec2): boolean {
  const steps = Math.max(1, Math.ceil(gap(a, b) / 0.5));
  let previous = a;
  for (let step = 1; step <= steps; step++) {
    const point: Vec2 = [
      a[0] + ((b[0] - a[0]) * step) / steps,
      a[1] + ((b[1] - a[1]) * step) / steps,
    ];
    if (!collision.contains(point)) return false;
    if (
      gap(collision.move(previous, [point[0] - previous[0], point[1] - previous[1]]), point) > 0.01
    )
      return false;
    previous = point;
  }
  return true;
}

export class VisitorFollow {
  target: string | null = null;
  private path: Vec2[] = [];
  private plannedAt = -Infinity;
  private catching = false;

  constructor(
    private readonly collision: CollisionWorld,
    private readonly net: () => WalkNet | null
  ) {}

  start(id: string): void {
    this.target = id;
    this.path = [];
    this.plannedAt = -Infinity;
  }

  stop(): void {
    this.target = null;
    this.path = [];
    this.catching = false;
  }

  input(
    state: ControlState,
    frame: InputFrame,
    peers: readonly PeerPose[],
    dt: number,
    now: number
  ): InputFrame {
    if (!this.target) return frame;
    const peer = peers.find((visitor) => visitor.id === this.target);
    if (!peer || peer.mode !== 'walk' || state.mode !== 'walk' || manualMovement(frame)) {
      this.stop();
      return frame;
    }
    const from: Vec2 = [state.pose.position[0], -state.pose.position[2]];
    const target: Vec2 = [peer.x, -peer.z];
    const distance = gap(from, target);
    if (distance > 60) {
      this.stop();
      return frame;
    }
    if (distance <= 2) return frame;
    if (now - this.plannedAt >= 500 || !this.path.length) {
      this.path = this.route(from, target);
      this.plannedAt = now;
    }
    while (this.path.length > 1 && this.path[0] && gap(from, this.path[0]) < 0.6) this.path.shift();
    const goal = this.path[0];
    if (!goal || dt <= 0) return frame;
    const yaw = Math.atan2(from[0] - goal[0], goal[1] - from[1]);
    const turn = Math.atan2(Math.sin(yaw - state.pose.yaw), Math.cos(yaw - state.pose.yaw));
    const delta = clamp(turn, -PLAYER.turnRadPerSecond * dt, PLAYER.turnRadPerSecond * dt);
    if (distance > 7) this.catching = true;
    if (distance < 3.5) this.catching = false;
    return {
      ...frame,
      forward: Math.abs(turn) > 0.8 ? 0 : Math.min(1, (distance - 2) / 0.75),
      boost: this.catching,
      lookDx: -delta / PLAYER.lookRadPerPx,
      turn: 0,
    };
  }

  private route(from: Vec2, target: Vec2): Vec2[] {
    if (clearFollowPath(this.collision, from, target)) return [target];
    const net = this.net();
    if (!net) return [];
    const first = nearestNode(net, from, 12),
      last = nearestNode(net, target, 12);
    if (first < 0 || last < 0) return [];
    const tree = routeTree(net, last);
    if (!Number.isFinite(tree.distance[first])) return [];
    const points: Vec2[] = [];
    for (
      let node = first, steps = 0;
      node >= 0 && steps < net.count;
      node = tree.next[node] ?? -1, steps++
    ) {
      const x = net.x[node],
        y = net.y[node];
      if (x === undefined || y === undefined)
        throw new Error('The city route references a missing point');
      points.push([x, y]);
      if (node === last) break;
    }
    points.push(target);
    let visible = -1;
    for (let i = 0; i < points.length; i++) {
      const point = points[i];
      if (!point || !clearFollowPath(this.collision, from, point)) break;
      visible = i;
    }
    return visible >= 0 ? points.slice(visible) : [];
  }
}
